import { Component, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import dayjs from 'dayjs';
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { ToasterService } from '../../../../core/services/toaster.service';
import { AuthService } from '../../../../core/services/auth.service';
import { SidebarService } from '../../../../core/services/sidebar.service';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { InputComponent } from '../../../../shared/components/ui/input/input.component';
import {
  DropdownComponent,
  DropdownOption,
} from '../../../../shared/components/ui/dropdown/dropdown.component';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { isRegisteredComponent } from '../../../../core/registry/component-registry';
import { AuditLogPanelComponent } from '../../../../shared/components/audit-log-panel/audit-log-panel.component';
import { AuditEntityType } from '../../../../core/models/audit-log.model';

interface ProductRoutes {
  product: { id: string; name: string };
  routes: any[];
}

/** Prism is the core CRM product; its routes come from its own API group. */
const PRISM_PRODUCT_ID = 'prism';

@Component({
  selector: 'app-app-routes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    InputComponent,
    DropdownComponent,
    IconComponentV2,
    AuditLogPanelComponent,
  ],
  templateUrl: './app-routes.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [':host { display: block; height: 100%; min-height: 0; }'],
})
export class AppRoutesComponent implements OnInit {
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);
  private dialog = inject(MatDialog);
  private toaster = inject(ToasterService);
  private authService = inject(AuthService);
  private sidebarService = inject(SidebarService);

  productRoutes = signal<ProductRoutes[]>([]);
  selectedProductId = signal<string>(PRISM_PRODUCT_ID);
  /** When true, the list shows deleted (INACTIVE) routes instead of active ones. */
  showDeleted = signal<boolean>(false);
  /** When true, the audit-logs screen replaces the routes view. */
  showAudit = signal<boolean>(false);
  auditEntityTypes = [AuditEntityType.APP_ROUTE];
  loading = signal<boolean>(false);
  /** Route ids that are expanded; everything else is collapsed by default. */
  expandedNodes = signal<Set<string>>(new Set());
  /** When true, the left panel switches from the routes list to the create/edit form. */
  isFormOpen = signal<boolean>(false);
  /** Id of the route being edited (null = create mode). */
  editId = signal<string | null>(null);
  /** The parent route a new sub-route is being created under (null = top-level). */
  createParent = signal<any>(null);
  submitting = signal<boolean>(false);

  isEditMode = computed(() => this.isFormOpen() && this.editId() !== null);
  /** Id of the preview item that represents the form's route (draft or edited). */
  formItemId = computed(() =>
    this.isEditMode() ? this.editId() : '__draft__'
  );

  // --- Create-route form state (drives the live sidebar preview) ---
  draftName = signal<string>('');
  draftParentId = signal<string | null>(null);
  draftPath = signal<string>('');
  draftIcon = signal<string>('');
  draftComponentName = signal<string>('');
  draftComponentPath = signal<string>('');
  draftOrder = signal<number>(0);
  draftIsPublic = signal<boolean>(false);
  draftShowInSidebar = signal<boolean>(true);

  /**
   * Routes in the selected product that can act as a parent: only grouping
   * routes with no component (blank componentName AND componentPath).
   */
  parentOptions = computed<DropdownOption[]>(() => {
    const out: DropdownOption[] = [];
    const walk = (nodes: any[], depth: number) => {
      for (const node of nodes) {
        const isParentEligible = !node.componentName && !node.componentPath;
        if (isParentEligible) {
          out.push({
            value: node.id,
            label: `${'— '.repeat(depth)}${node.name}`,
          });
        }
        if (node.subMenus?.length) walk(node.subMenus, depth + 1);
      }
    };
    walk(this.selectedRootRoutes(), 0);
    return out;
  });

  /** Real product id (from the API group) for the current selection. */
  private selectedProductApiId = computed(() => {
    const name = this.products
      .find((p) => p.id === this.selectedProductId())
      ?.name?.toLowerCase();
    return (
      this.productRoutes().find(
        (g) => g.product?.name?.toLowerCase() === name
      )?.product?.id ?? null
    );
  });

  /** Every route (all depths) within the selected product — used for uniqueness checks. */
  private allProductRoutesFlat = computed(() => {
    const out: any[] = [];
    const walk = (nodes: any[]) => {
      for (const node of nodes) {
        out.push(node);
        if (node.subMenus?.length) walk(node.subMenus);
      }
    };
    walk(this.selectedRootRoutes());
    return out;
  });

  /** Duplicate route name within the selected product (ignores the edited route). */
  nameError = computed(() => {
    const name = this.draftName().trim().toLowerCase();
    if (!name) return false;
    const editId = this.editId();
    return this.allProductRoutesFlat().some(
      (r) => r.id !== editId && (r.name || '').trim().toLowerCase() === name
    );
  });

  /** Duplicate component name within the selected product (ignores the edited route). */
  componentNameError = computed(() => {
    const cn = this.draftComponentName().trim().toLowerCase();
    if (!cn) return false;
    const editId = this.editId();
    return this.allProductRoutesFlat().some(
      (r) => r.id !== editId && (r.componentName || '').trim().toLowerCase() === cn
    );
  });

  isCreateValid = computed(
    () =>
      this.draftName().trim().length > 0 &&
      this.draftPath().trim().length > 0 &&
      !this.nameError() &&
      !this.componentNameError()
  );

  /** Static component-path prefix, driven by the visibility toggle. */
  componentPathPrefix = computed(
    () => `pages/${this.draftIsPublic() ? 'public' : 'private'}/`
  );

/** Product tags: Prism (core CRM) first, then every configured product. */
  products = [
    { id: PRISM_PRODUCT_ID, name: 'Prism' },
    ...this.productConfigService.getProductConfigs().map((p) => ({
      id: p.id,
      name: p.displayName,
    })),
  ];

  /**
   * Top-level routes for the selected product. Routes arrive grouped by product
   * (same shape as Page Routes), so we match the selection to its product group
   * by name — every product, including Prism, shows only its own routes.
   */
  private selectedRootRoutes = computed(() => {
    const name = this.products
      .find((p) => p.id === this.selectedProductId())
      ?.name?.toLowerCase();
    return this.productRoutes()
      .filter((g) => g.product?.name?.toLowerCase() === name)
      .flatMap((g) => g.routes || []);
  });

  /** Nested route tree for the selected product. */
  routeTree = computed(() => this.selectedRootRoutes());

  totalRoutes = computed(() => this.countRoutes(this.routeTree()));

  /** Tracks which preview menu items are expanded. */
  previewExpanded = signal<Set<string>>(new Set());

  /**
   * Mirrors the real sidebar build (loadMenuByProductId): only routes with
   * onlyPath === false become menu items, and sub-items with onlyPath === true
   * are hidden. This is a true preview of the sidebar for the selected product.
   */
  /**
   * Source tree for the preview. In edit mode the edited route is re-applied
   * (fields, parent, position) so its changes appear live in the preview.
   */
  private previewSource = computed(() => {
    const routes = structuredClone(this.selectedRootRoutes());
    if (!this.isEditMode()) return routes;

    const node = this.extractNode(routes, this.editId()!);
    if (!node) return routes;

    node.name = this.draftName().trim() || node.name;
    node.icon = this.draftIcon().trim() || null;
    node.onlyPath = !this.draftShowInSidebar();
    node.isPublic = this.draftIsPublic();
    node.componentName = this.draftComponentName().trim() || null;

    const parentId = this.draftParentId();
    let siblings = routes;
    if (parentId) {
      const parent = this.findNode(routes, parentId);
      if (parent) siblings = parent.subMenus ??= [];
    }
    const at = Math.min(Math.max(this.draftOrder(), 0), siblings.length);
    siblings.splice(at, 0, node);
    return routes;
  });

  sidebarPreview = computed(() => {
    const editId = this.isEditMode() ? this.editId() : null;
    const base = this.previewSource()
      .filter((r) => r.onlyPath === false && this.isRenderable(r))
      .map((r) => ({
        id: r.id,
        label: r.name,
        icon: this.previewIcon(r),
        route: r,
        isDraft: r.id === editId,
        children: (r.subMenus || [])
          .filter((s: any) => s.onlyPath !== true && this.isRenderable(s))
          .map((s: any) => ({
            id: s.id,
            label: s.name,
            route: s,
            isDraft: s.id === editId,
          })),
      }));

    // Only create mode adds a separate draft item (edit applies in previewSource).
    if (this.isEditMode() || !this.isFormOpen() || !this.draftShowInSidebar()) {
      return base;
    }

    const draftLabel = this.draftName().trim() || 'New route';
    const parentId = this.draftParentId();
    const at = (len: number) =>
      Math.min(Math.max(this.draftOrder(), 0), len);

    if (parentId) {
      const draftChild = {
        id: '__draft__',
        label: draftLabel,
        route: null,
        isDraft: true,
      };
      return base.map((item) => {
        if (item.id !== parentId) return item;
        const children = [...item.children];
        children.splice(at(children.length), 0, draftChild);
        return { ...item, children };
      });
    }

    const items = [...base];
    items.splice(at(items.length), 0, {
      id: '__draft__',
      label: draftLabel,
      icon: this.previewIcon({ icon: this.draftIcon() }),
      route: null,
      isDraft: true,
      children: [],
    });
    return items;
  });

  /** Finds and removes a node from the tree, returning it (or undefined). */
  private extractNode(nodes: any[], id: string): any | undefined {
    const idx = nodes.findIndex((n) => n.id === id);
    if (idx >= 0) return nodes.splice(idx, 1)[0];
    for (const node of nodes) {
      if (node.subMenus?.length) {
        const found = this.extractNode(node.subMenus, id);
        if (found) return found;
      }
    }
    return undefined;
  }

  /**
   * Whether a route would get an Angular route (and thus appear in the sidebar):
   * a leaf needs its component registered; a parent needs a renderable child.
   * Mirrors the sidebar's own logic so the preview matches reality.
   */
  private isRenderable(route: any): boolean {
    if (route?.subMenus?.length) {
      return route.subMenus.some((sub: any) => this.isRenderable(sub));
    }
    return isRegisteredComponent(route?.componentName);
  }

  ngOnInit(): void {
    // Always re-fetch on entry (bypass cache) so the list is fresh.
    this.loadRoutes(true);
  }

  selectProduct(id: string): void {
    this.selectedProductId.set(id);
  }

  toggleDeleted(): void {
    this.showDeleted.update((v) => !v);
    this.loadRoutes(true);
  }

  recoverRoute(route: any): void {
    this.apiService
      .put(
        '/app-routes/update-status',
        { id: route.id, status: 'ACTIVE' },
        { showLoader: true, showErrorToaster: true, showSuccessToaster: false }
      )
      .subscribe({
        next: () => {
          this.toaster.success('Route recovered successfully', 'Success', 2000);
          this.loadRoutes(true);
          this.refreshSidebar();
        },
      });
  }

  refresh(): void {
    this.loadRoutes(true);
  }

  isExpanded(id: string): boolean {
    return this.expandedNodes().has(id);
  }

  toggleNode(id: string): void {
    this.expandedNodes.update((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  expandAll(): void {
    const ids = new Set<string>();
    const walk = (nodes: any[]) => {
      for (const node of nodes) {
        if (node.subMenus?.length) {
          ids.add(node.id);
          walk(node.subMenus);
        }
      }
    };
    walk(this.routeTree());
    this.expandedNodes.set(ids);
  }

  collapseAll(): void {
    this.expandedNodes.set(new Set());
  }

  /** True when at least one route in the current tree has children. */
  hasNesting = computed(() =>
    this.routeTree().some((r) => r.subMenus?.length)
  );

  formatDate(date: string): string {
    return date ? dayjs(date).format('MMM D, YYYY') : '--';
  }

  /** The route's configured icon, with a folder/file fallback when none is set. */
  iconFor(route: any): string {
    return route?.icon || (route?.subMenus?.length ? 'folder' : 'file-text');
  }

  /** The icon the sidebar would render for a menu item (route icon or default). */
  previewIcon(route: any): string {
    const icon = typeof route?.icon === 'string' ? route.icon.trim() : '';
    return icon || 'layoutDashboard';
  }

  /**
   * True when a navigable (leaf) route has no matching component in the
   * COMPONENT_REGISTRY, so it would fail to load. Parent/group routes that only
   * contain sub-routes don't need their own component, so they're never flagged.
   */
  isUnconfigured(route: any): boolean {
    if (route?.subMenus?.length) return false;
    return !isRegisteredComponent(route?.componentName);
  }

  isPreviewExpanded(id: string): boolean {
    return this.previewExpanded().has(id);
  }

  // --- Drag & drop reordering in the sidebar preview ---
  private dragId = signal<string | null>(null);
  private dragParentId = signal<string | null>(null);
  dragOverId = signal<string | null>(null);

  onPreviewDragStart(id: string, parentId: string | null): void {
    // While a form is open only its route (draft / edited) can be dragged.
    if (this.isFormOpen() && id !== this.formItemId()) return;
    this.dragId.set(id);
    this.dragParentId.set(parentId);
  }

  onPreviewDragEnter(id: string, parentId: string | null): void {
    const dragging = this.dragId();
    if (!dragging || dragging === id) return;
    // The draft can drop anywhere; persisted reorders stay within one level.
    if (this.isFormOpen() || this.dragParentId() === parentId) {
      this.dragOverId.set(id);
    }
  }

  onPreviewDragEnd(): void {
    this.dragId.set(null);
    this.dragParentId.set(null);
    this.dragOverId.set(null);
  }

  onPreviewDrop(targetId: string, parentId: string | null): void {
    const draggedId = this.dragId();
    this.dragOverId.set(null);
    if (!draggedId || draggedId === targetId) {
      this.onPreviewDragEnd();
      return;
    }

    if (this.isFormOpen()) {
      if (draggedId === this.formItemId()) this.positionDraft(parentId, targetId);
      this.onPreviewDragEnd();
      return;
    }

    if (this.dragParentId() !== parentId) {
      this.onPreviewDragEnd();
      return;
    }
    this.reorderAndPersist(parentId, draggedId, targetId);
    this.onPreviewDragEnd();
  }

  /**
   * Drops the draft route: dropping onto a parent (or among its children)
   * nests it and auto-fills the parent; dropping at top level un-nests it.
   * The drop position becomes the draft's order.
   */
  private positionDraft(levelParentId: string | null, targetId: string): void {
    if (!levelParentId) {
      const target = this.sidebarPreview().find((i) => i.id === targetId);
      if (target && !target.isDraft && target.children?.length) {
        // Nest inside this parent menu.
        this.draftParentId.set(targetId);
        this.draftOrder.set(this.previewSiblings(targetId).length);
        this.previewExpanded.update((s) => new Set(s).add(targetId));
        return;
      }
      this.draftParentId.set(null);
    } else {
      this.draftParentId.set(levelParentId);
    }
    const siblings = this.previewSiblings(this.draftParentId());
    const idx = siblings.findIndex((s) => s.id === targetId);
    this.draftOrder.set(idx >= 0 ? idx : siblings.length);
  }

  /** Non-draft preview items at the given level (top-level or under a parent). */
  private previewSiblings(parentId: string | null): any[] {
    const preview = this.sidebarPreview();
    if (!parentId) return preview.filter((i) => !i.isDraft);
    const parent = preview.find((i) => i.id === parentId);
    return (parent?.children || []).filter((c: any) => !c.isDraft);
  }

  /** Rebuilds the real app sidebar + dynamic routes after a mutation. */
  private refreshSidebar(): void {
    this.authService.authenticate(true).subscribe((ok) => {
      if (ok) this.sidebarService.callSidebarMethod();
    });
  }

  private reorderAndPersist(
    parentId: string | null,
    draggedId: string,
    targetId: string
  ): void {
    const data = structuredClone(this.productRoutes());
    const group = this.matchGroup(data);
    if (!group) return;

    const siblings: any[] | undefined = parentId
      ? this.findNode(group.routes || [], parentId)?.subMenus
      : group.routes;
    if (!siblings) return;

    const from = siblings.findIndex((s) => s.id === draggedId);
    const to = siblings.findIndex((s) => s.id === targetId);
    if (from < 0 || to < 0) return;

    const [moved] = siblings.splice(from, 1);
    siblings.splice(to, 0, moved);
    siblings.forEach((s, i) => (s.sortOrder = i));

    // Optimistic local update so the preview + tree reorder instantly.
    this.productRoutes.set(data);
    this.persistOrder(group, siblings);
  }

  private matchGroup(data: ProductRoutes[]): ProductRoutes | undefined {
    const name = this.products
      .find((p) => p.id === this.selectedProductId())
      ?.name?.toLowerCase();
    return data.find((g) => g.product?.name?.toLowerCase() === name);
  }

  private findNode(nodes: any[], id: string): any | undefined {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = node.subMenus?.length
        ? this.findNode(node.subMenus, id)
        : undefined;
      if (found) return found;
    }
    return undefined;
  }

  private persistOrder(group: ProductRoutes, siblings: any[]): void {
    const calls = siblings.map((s) => {
      const payload: any = {
        id: s.id,
        name: s.name,
        path: s.path,
        productId: group.product?.id,
        icon: s.icon ?? null,
        sortOrder: s.sortOrder,
        status: s.status,
        onlyPath: s.onlyPath,
        isPublic: s.isPublic,
        componentName: s.componentName ?? null,
        componentPath: s.componentPath ?? null,
        moduleName: s.module?.name ?? null,
      };
      if (s.parentId) payload.parentId = s.parentId;
      return this.apiService.put('/app-routes/update-route', payload, {
        showLoader: false,
        showErrorToaster: true,
        showSuccessToaster: false,
      });
    });

    if (!calls.length) return;
    forkJoin(calls).subscribe({
      next: () => {
        this.toaster.success('Order updated', 'Success', 1500);
        this.refreshSidebar();
      },
      error: () => this.loadRoutes(true), // revert to server state on failure
    });
  }

  togglePreview(id: string): void {
    this.previewExpanded.update((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  addRoute(parent?: any): void {
    this.resetDraft();
    this.editId.set(null);
    this.createParent.set(parent ?? null);
    this.draftParentId.set(parent?.id ?? null);
    this.isFormOpen.set(true);
  }

  /** Opens the form prefilled with an existing route's values (edit mode). */
  editRoute(route: any): void {
    this.resetDraft();
    this.editId.set(route.id);
    this.createParent.set(null);
    this.draftName.set(route.name || '');
    this.draftPath.set(route.path || '');
    this.draftIcon.set(route.icon || '');
    this.draftComponentName.set(route.componentName || '');
    this.draftComponentPath.set(
      (route.componentPath || '').replace(/^pages\/(public|private)\//, '')
    );
    this.draftIsPublic.set(!!route.isPublic);
    this.draftShowInSidebar.set(route.onlyPath !== true);
    this.draftParentId.set(route.parentId || null);
    this.draftOrder.set(this.currentIndexOf(route));
    this.isFormOpen.set(true);
  }

  /** Closes the form and re-fetches routes so the preview reflects the server. */
  cancelCreate(): void {
    this.isFormOpen.set(false);
    this.editId.set(null);
    this.createParent.set(null);
    this.resetDraft();
    this.loadRoutes(true);
  }

  /** Index of a route among its current siblings (for prefilling the edit order). */
  private currentIndexOf(route: any): number {
    const roots = this.selectedRootRoutes();
    const siblings = route.parentId
      ? this.findNode(roots, route.parentId)?.subMenus || []
      : roots;
    const idx = siblings.findIndex((s: any) => s.id === route.id);
    return idx >= 0 ? idx : siblings.length;
  }

  private resetDraft(): void {
    this.draftName.set('');
    this.draftParentId.set(null);
    this.draftPath.set('');
    this.draftIcon.set('');
    this.draftComponentName.set('');
    this.draftComponentPath.set('');
    // Large value → the draft starts at the end of its sibling list (drag to move).
    this.draftOrder.set(Number.MAX_SAFE_INTEGER);
    this.draftIsPublic.set(false); // private by default
    this.draftShowInSidebar.set(true);
  }

  /** Builds the create/update payload (mirrors Page Routes, module deprecated). */
  private buildRoutePayload(): any {
    // Resolve the draft's drag position into a concrete sort order.
    const siblings = this.previewSiblings(this.draftParentId());
    const sortOrder = Math.min(
      Math.max(this.draftOrder(), 0),
      siblings.length
    );

    // A component path is only sent when a component name is provided.
    const componentName = this.draftComponentName().trim() || null;
    let componentPath: string | null = null;
    if (componentName) {
      const relative = this.draftComponentPath()
        .trim()
        .replace(/^pages\/(public|private)\//, '')
        .replace(/^\/+/, '');
      componentPath = `${this.componentPathPrefix()}${relative}`;
    }

    const payload: any = {
      path: this.draftPath().trim().replace(/^\/+/, ''),
      name: this.draftName().trim(),
      productId: this.selectedProductApiId(),
      icon: this.draftIcon().trim() || null,
      sortOrder,
      status: 'ACTIVE',
      onlyPath: !this.draftShowInSidebar(),
      componentPath,
      componentName,
      isPublic: this.draftIsPublic(),
    };
    if (this.draftParentId()) payload.parentId = this.draftParentId();
    return payload;
  }

  submitForm(): void {
    if (!this.isCreateValid() || this.submitting()) return;
    const payload = this.buildRoutePayload();
    const editing = this.isEditMode();
    if (editing) payload.id = this.editId();

    this.submitting.set(true);
    const request$ = editing
      ? this.apiService.put('/app-routes/update-route', payload, {
          showSuccessToaster: false,
          showLoader: true,
        })
      : this.apiService.post('/app-routes/create-route', payload, {
          showSuccessToaster: false,
          showLoader: true,
        });

    request$.subscribe({
      next: () => {
        this.toaster.success(
          editing ? 'Route updated successfully' : 'Route created successfully',
          'Success',
          2000
        );
        this.submitting.set(false);
        this.refreshSidebar();
        this.cancelCreate();
      },
      error: (err: any) => {
        this.submitting.set(false);
        this.toaster.error(
          err?.error?.message ||
            err?.message ||
            `Failed to ${editing ? 'update' : 'create'} route`,
          'Error',
          2500
        );
      },
    });
  }

  deleteRoute(route: any): void {
    this.dialog
      .open(ConfirmationModalComponent, {
        width: '400px',
        data: {
          title: 'Confirm Deletion',
          description: `Are you sure you want to delete "${route?.name}"?`,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.apiService
          .delete(`app-routes/delete-route/${route.id}`, {
            showSuccessToaster: true,
            showLoader: true,
          })
          .subscribe({
            next: () => {
              this.toaster.success('Route deleted successfully', 'Success', 2000);
              this.loadRoutes(true);
              this.refreshSidebar();
            },
          });
      });
  }

  private loadRoutes(refresh: boolean = false): void {
    this.loading.set(true);
    this.apiService
      .get(
        'app-routes/list-routes',
        {
          parentId: null,
          productId: null,
          status: this.showDeleted() ? 'INACTIVE' : 'ACTIVE',
          sortOrder: 'asc',
        },
        {
          showErrorToaster: true,
          showSuccessToaster: false,
          showLoader: true,
          cache: !refresh,
        }
      )
      .subscribe({
        next: (res: any) => {
          let data = res?.data ?? res;
          if (!Array.isArray(data)) data = [data];
          this.productRoutes.set(data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  /** Counts every route across the nested tree. */
  private countRoutes(nodes: any[]): number {
    return nodes.reduce(
      (total, node) => total + 1 + this.countRoutes(node.subMenus || []),
      0
    );
  }
}
